import store, { useAppSelector } from "../../../global/utils/redux/store";
import { useCallback, useEffect } from "react";

import defaultsFile from "../../../assets/langs/defaults.json";

import { userSelectedLanguage } from "../../../global/utils/redux/globalSlice";
import { setLanguageInSession } from "../../../global/utils/api/useSetLanguageInSession";
import { StringParam, useQueryParam } from "use-query-params";
import { createSelector } from "@reduxjs/toolkit";
import isQr from "../../../global/utils/helpers/isQr.tsx";
import { Language } from "@jamezz/react-components";

export const selectAvailableLanguages = createSelector(
  [(state) => state.global.sessionState?.showLangselLanguages, (state) => state.global.salesarea.applicationLanguage],
  (languagesString, applicationLanguage) => {
    const languages = (languagesString ? (JSON.parse(languagesString) as Language[]) : null) ?? [];
    if (!languages.includes(applicationLanguage) && applicationLanguage) {
      languages.push(applicationLanguage);
    }
    return languages;
  }
);

export function languageIsValid(language: Language, languages: Language[]) {
  return languages.includes(language);
}

export const selectAppLanguage = createSelector(
  [
    (state) => state.global.sessionState?.lang,
    (state) => state.global.userSelectedLanguage,
    selectAvailableLanguages,
    (state) => state.global.salesarea.applicationLanguage,
  ],
  (sessionLanguage, selectedLanguage, availableLanguages, applicationLanguage): Language => {
    if (selectedLanguage && languageIsValid(selectedLanguage, availableLanguages)) {
      return selectedLanguage;
    }
    if (sessionLanguage && languageIsValid(sessionLanguage, availableLanguages)) {
      return sessionLanguage as Language;
    }

    return applicationLanguage ?? availableLanguages[0] ?? "en";
  }
);

export function setLanguage(lang: Language) {
  setLanguageInSession(lang);
  store.dispatch(userSelectedLanguage(lang));
}

function useLanguage() {
  const availableLanguages = useAppSelector(selectAvailableLanguages);

  const appLanguage = useAppSelector(selectAppLanguage);

  useEffect(() => {
    const htmlElements = document.getElementsByTagName("html");
    for (const htmlElement of htmlElements) {
      htmlElement.setAttribute("lang", appLanguage);
    }
  }, [appLanguage]);

  useEffect(() => {
    if (isQr()) {
      const lang: Language = navigator.language.substr(0, 2) as Language;
      if (sessionStorage.getItem("V5.userSelectedLanguage") == null && languageIsValid(lang, availableLanguages)) {
        setLanguage(lang);
      }
    }
  }, [availableLanguages]);

  const [languageQueryParam] = useQueryParam("language", StringParam);
  useEffect(() => {
    if (languageQueryParam) setLanguage(languageQueryParam as Language);
  }, [languageQueryParam]);
}

export function useResetLanguage() {
  const applicationLanguage = useAppSelector((state) => state.global.salesarea?.applicationLanguage);
  const [languageQueryParam] = useQueryParam("language", StringParam);
  return useCallback(() => {
    if (!languageQueryParam) {
      setLanguage(applicationLanguage);
    }
  }, [applicationLanguage, languageQueryParam]);
}

export const defaultMessages = defaultsFile;

export default useLanguage;
